import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '../../lib/cn';
import { Dialog, DialogTitle, DialogContent, DialogDescription } from '../Dialog';

// The console's confirm popup. No close button by design: every dismissal is an
// explicit action.
export const KitDialog = ({ open, onOpenChange, className, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      showClose={false}
      overlayClassName="bg-[rgba(5,8,9,0.8)]"
      className={cn(
        'w-[329px] max-w-[329px] gap-6 rounded-[16px] border-0 bg-dusq-raised p-6 font-dusq',
        className
      )}
    >
      {children}
    </DialogContent>
  </Dialog>
);

/** The centred body copy. Doubles as the accessible title when no heading is shown. */
export const KitDialogMessage = ({ children }) => (
  <>
    <DialogTitle className="sr-only">{children}</DialogTitle>
    <DialogDescription className="text-center font-dusq text-[14px] leading-normal text-dusq-emphasis-mid">
      {children}
    </DialogDescription>
  </>
);

export const kitDialogActionVariants = cva(
  'flex h-[50px] w-full items-center justify-center rounded-[16px] px-8 font-dusq text-[14px] font-medium transition-[opacity,transform] active:scale-[0.98] motion-reduce:active:scale-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dusq-accent disabled:opacity-50',
  {
    variants: {
      // Both fills are the design's two-layer gradients, kept verbatim so the warm
      // tint under the primary's near-white survives.
      tone: {
        primary: 'text-dusq-emphasis-high-dark hover:opacity-90',
        secondary: 'text-dusq-emphasis-high hover:opacity-80',
      },
    },
    defaultVariants: { tone: 'secondary' },
  }
);

const ACTION_FILL = {
  primary:
    'linear-gradient(90deg, rgba(250,250,250,0.95) 0%, rgba(250,250,250,0.95) 100%), linear-gradient(90deg, rgb(215,184,149) 0%, rgb(215,184,149) 100%)',
  secondary:
    'linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.05) 100%), linear-gradient(90deg, rgb(5,8,9) 0%, rgb(5,8,9) 100%)',
};

export const KitDialogAction = React.forwardRef(
  ({ className, tone = 'secondary', ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      style={{ backgroundImage: ACTION_FILL[tone] }}
      className={cn(kitDialogActionVariants({ tone }), className)}
      {...props}
    />
  )
);
KitDialogAction.displayName = 'KitDialogAction';

/** Actions stack full width with the design's 16px gap. */
export const KitDialogActions = ({ children }) => (
  <div className="flex w-full flex-col gap-4">{children}</div>
);
